// Top-level router. The shared Layout wraps every route; protected pages are
// guarded by <ProtectedRoute>.

import React, { Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "./components/Layout";
import PageLoader from "./components/PageLoader";
import ProtectedRoute from "./auth/ProtectedRoute";
import AdminRoute from "./auth/AdminRoute";

// Eager imports — entry-point pages that users hit first
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";

// Lazy-loaded pages
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Settings = React.lazy(() => import("./pages/Settings"));
const EsoftCompanies = React.lazy(() => import("./pages/EsoftCompanies"));
const EsoftCompanyDetail = React.lazy(() => import("./pages/EsoftCompanyDetail"));
const Wallet = React.lazy(() => import("./pages/Wallet"));
const TopupSuccess = React.lazy(() => import("./pages/TopupSuccess"));
const TopupCancel = React.lazy(() => import("./pages/TopupCancel"));
const Upload = React.lazy(() => import("./pages/Upload"));
const Batches = React.lazy(() => import("./pages/Batches"));
const BatchDetail = React.lazy(() => import("./pages/BatchDetail"));
const BatchFileDetail = React.lazy(() => import("./pages/BatchFileDetail"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const GoogleCallback = React.lazy(() => import("./pages/GoogleCallback"));
const MicrosoftCallback = React.lazy(() => import("./pages/MicrosoftCallback"));
const CancelDeletion = React.lazy(() => import("./pages/CancelDeletion"));
const Impersonate = React.lazy(() => import("./pages/Impersonate"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const Support = React.lazy(() => import("./pages/Support"));
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = React.lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminAuditLogs = React.lazy(() => import("./pages/admin/AdminAuditLogs"));

export default function App() {
  const { t } = useTranslation("common");
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route
          path="*"
          element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/google/callback" element={<GoogleCallback />} />
                  <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/cancel-deletion" element={<CancelDeletion />} />
                  <Route path="/impersonate" element={<Impersonate />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route path="/support" element={<Support />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wallet"
                    element={
                      <ProtectedRoute>
                        <Wallet />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wallet/topup/success"
                    element={
                      <ProtectedRoute>
                        <TopupSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wallet/topup/cancel"
                    element={
                      <ProtectedRoute>
                        <TopupCancel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute>
                        <Upload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/batches"
                    element={
                      <ProtectedRoute>
                        <Batches />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/batches/:id"
                    element={
                      <ProtectedRoute>
                        <BatchDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/batches/:id/files/:fileId"
                    element={
                      <ProtectedRoute>
                        <BatchFileDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/esoft"
                    element={
                      <ProtectedRoute>
                        <EsoftCompanies />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/esoft/:id"
                    element={
                      <ProtectedRoute>
                        <EsoftCompanyDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="users/:userId" element={<AdminUserDetail />} />
                    <Route path="audit" element={<AdminAuditLogs />} />
                  </Route>
                  <Route
                    path="*"
                    element={
                      <div className="py-12 text-center text-slate-500">{t("pageNotFound")}</div>
                    }
                  />
                </Routes>
              </Suspense>
            </Layout>
          }
        />
      </Routes>
    </Suspense>
  );
}
